import { useEffect, useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useContentToken, usePropertyField, EditableToken } from '@vibesites/runtime/react';

// Starting height before the embed reports its own size. Kept tight to the
// scheduler's actual content (AnyoneHome doesn't reliably post a height, so
// this default is what most visitors see); the postMessage listener below
// grows it to the real content height if/when a height is reported.
const DEFAULT_HEIGHT = 450;

// Pull a numeric pixel height out of the various message shapes embed widgets
// use (`{ height }`, `{ type: 'resize', height }`, iframe-resizer's
// `[iFrameSizer]...:<h>:`), ignoring anything that doesn't look like a height.
function parseFrameHeight(data: unknown): number | null {
  if (typeof data === 'number' && Number.isFinite(data) && data > 0) return data;
  if (typeof data === 'string') {
    const m = data.match(/(\d+(?:\.\d+)?)/);
    if (m) {
      const n = Number(m[1]);
      return n > 0 ? n : null;
    }
    return null;
  }
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    const h = obj.height ?? obj.iframeHeight ?? obj['iframe-height'];
    if (typeof h === 'number' && Number.isFinite(h) && h > 0) return h;
    if (typeof h === 'string') {
      const n = Number.parseFloat(h);
      return Number.isFinite(n) && n > 0 ? n : null;
    }
  }
  return null;
}

// The AnyoneHome scheduler is embedded via a per-property hosted URL. The
// `custom_anyonehome_scheduler_url` custom field holds the community-specific
// embed src — empty on properties that haven't been provisioned with one.
export function AHScheduler() {
  const heading = useContentToken('ahscheduler.heading');

  const schedulerUrl = (usePropertyField('custom_anyonehome_scheduler_url') as string | null) || '';

  const [height, setHeight] = useState(DEFAULT_HEIGHT);

  // Only accept resize messages from the scheduler's own origin.
  const schedulerOrigin = (() => {
    try {
      return schedulerUrl ? new URL(schedulerUrl).origin : '';
    } catch {
      return '';
    }
  })();

  useEffect(() => {
    if (!schedulerUrl || typeof window === 'undefined') return;

    const onMessage = (event: MessageEvent) => {
      if (schedulerOrigin && event.origin !== schedulerOrigin) return;
      const h = parseFrameHeight(event.data);
      // Guard against bogus zero/near-zero heights collapsing the embed.
      if (h && h > 100) setHeight(Math.ceil(h));
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [schedulerUrl, schedulerOrigin]);

  // Nothing to embed until a property is assigned a scheduler URL — render the
  // optional heading anchor only so the component still exposes its binding.
  return (
    <section className="bg-white pt-2 pb-3 md:pt-3 md:pb-4">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Optional heading — empty by default, so no heading renders unless an
            editor sets one. Kept as the component's editable binding. */}
        {heading ? (
          <EditableToken
            path="ahscheduler.heading"
            as="h2"
            className="font-heading text-2xl md:text-3xl text-[var(--vibe-color-text)] mb-6 text-center"
          >
            {heading}
          </EditableToken>
        ) : null}

        {schedulerUrl ? (
          <iframe
            title="Schedule a Tour"
            src={schedulerUrl}
            name="anyoneHomeScheduler"
            style={{ border: 'none', display: 'block', width: '100%', height: `${height}px` }}
          />
        ) : null}
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'AHScheduler',
  displayName: 'AnyoneHome Scheduler',
  tokens: {
    'ahscheduler.heading': {
      label: 'Heading (optional)',
      type: 'text',
      defaultValue: '',
    },
  },
};

(AHScheduler as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default AHScheduler;
